import { useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { ILLUSTRATIONS } from '@constants/illustrations';
import { COMMON, FORBIDDEN, NOT_FOUND, OUT_OF_SCOPE_FILTER, PAGE_FAILED } from '@locales/id/messages';
import { Button, ButtonLink } from './Button';
import { EmptyState } from './EmptyState';
import { ErrorBoundary } from './ErrorBoundary';

/** Unknown address inside an area. */
export function NotFound({ home }: { home: string }) {
  return (
    <EmptyState icon="search" illustration={ILLUSTRATIONS.notFound} title={NOT_FOUND.title} action={<ButtonLink to={home} variant="primary">{COMMON.backHome}</ButtonLink>}>
      {NOT_FOUND.text}
    </EmptyState>
  );
}

/** A known page this account's role may not open (403): said as such, never as a missing page. */
export function Forbidden({ home }: { home: string }) {
  return (
    <EmptyState icon="lock" illustration={ILLUSTRATIONS.stateForbidden} title={FORBIDDEN.title} action={<ButtonLink to={home} variant="primary">{COMMON.backHome}</ButtonLink>}>
      {FORBIDDEN.text}
    </EmptyState>
  );
}

/**
 * A record that is not there for this account: deleted, a mistyped link, or outside its region (the backend does not
 * tell which, on purpose). Says so in plain words and leads back to the list.
 */
export function RecordMissing({ title, back, backLabel }: { title: string; back: string; backLabel: string }) {
  return (
    <EmptyState icon="search" illustration={ILLUSTRATIONS.notFound} title={title} action={<ButtonLink to={back} variant="primary">{backLabel}</ButtonLink>}>
      {NOT_FOUND.record}
    </EmptyState>
  );
}

/** A list whose filter names a place outside the account's region (an old or shared link): say so, offer to clear it. */
export function FilterOutOfScope({ onClear }: { onClear: () => void }) {
  return (
    <EmptyState icon="lock" illustration={ILLUSTRATIONS.stateForbidden} title={OUT_OF_SCOPE_FILTER.title} action={<Button variant="primary" onClick={onClear}>{COMMON.clearFilters}</Button>}>
      {OUT_OF_SCOPE_FILTER.text}
    </EmptyState>
  );
}

function PageFailed({ home }: { home: string }) {
  return (
    <EmptyState
      icon="warn"
      illustration={ILLUSTRATIONS.stateError}
      title={PAGE_FAILED.title}
      action={
        <>
          <Button variant="primary" icon="refresh" onClick={() => window.location.reload()}>{PAGE_FAILED.reload}</Button>
          <ButtonLink to={home}>{COMMON.backHome}</ButtonLink>
        </>
      }
    >
      {PAGE_FAILED.text}
    </EmptyState>
  );
}

/** One page failing to render (or its chunk failing to load) shows a friendly fallback; navigating resets it. */
export function RouteBoundary({ home, children }: { home: string; children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary resetKey={pathname} fallback={<PageFailed home={home} />}>
      {children}
    </ErrorBoundary>
  );
}
